import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  RefreshCcw as RefreshIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import EmailMatcher from "../../components/email-matcher/EmailMatcher";
import { ProcessedEmail } from "../../components/email-matcher/types";
import EmailDialog from "../../components/mail-dialog/MailDialog";
import { useGraphApi } from "../../store/graph/useGraphApi";

// Interface för mapp-information
interface MailFolder {
  id: string;
  displayName: string;
  parentFolderId: string;
  childFolderCount: number;
  unreadItemCount: number;
  totalItemCount: number;
}

// Interface för användarinformation
interface UserInfo {
  mail: string;
  userPrincipalName: string;
  displayName: string;
}

// Interface för e-post med parentFolderId
interface EnhancedEmailMessage extends ProcessedEmail {
  parentFolderId?: string;
}

const RecentEmails = () => {
  const [emails, setEmails] = useState<EnhancedEmailMessage[]>([]);
  const [folderMap, setFolderMap] = useState<Record<string, string>>({});
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // 7 dagar sedan som standard
  );
  const { callGraphApi, isRefreshing, lastRefreshError } = useGraphApi(); // Använd den nya hooken
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  // Uppdatera eventuellt fel från token-förnyelse
  useEffect(() => {
    if (lastRefreshError) {
      setError(`Token-förnyelse misslyckades: ${lastRefreshError}`);
    }
  }, [lastRefreshError]);

  // Hämta användarinformation när komponenten laddas
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await callGraphApi(
          "/me?$select=mail,userPrincipalName,displayName"
        );
        setUserInfo(data);
        console.log("User info:", data);
      } catch (err) {
        setError("Det gick inte att hämta användarinformation");
        console.error("Error fetching user info:", err);
      }
    };

    // Hämta mappar för att klassificera e-post
    const fetchFolders = async () => {
      try {
        const data = await callGraphApi("/me/mailFolders");

        // Skapa en mappning mellan folder-ID och displayName (lowercase för enklare jämförelse)
        const newFolderMap: Record<string, string> = {};
        data.value.forEach((folder: MailFolder) => {
          newFolderMap[folder.id] = folder.displayName.toLowerCase();
        });
        setFolderMap(newFolderMap);
        console.log("Folder mapping:", newFolderMap);
      } catch (err) {
        setError("Det gick inte att hämta mappinformation");
        console.error("Error fetching folders:", err);
      }
    };

    if (!isRefreshing) {
      fetchUserInfo();
      fetchFolders();
    }
  }, [callGraphApi, isRefreshing]);
  
  // Hämta e-post när nödvändig data är laddad eller datumet ändras
  useEffect(() => {
    const fetchEmails = async () => {
      if (!startDate) return;

      setLoading(true);
      setError(null);

      try {
        const formattedDate = startDate.toISOString();
        // Inkludera parentFolderId i API-anropet
        const data = await callGraphApi(
          `/me/messages?$filter=receivedDateTime ge ${formattedDate}&$orderby=receivedDateTime desc&$top=50&$select=id,subject,bodyPreview,receivedDateTime,from,toRecipients,hasAttachments,webLink,importance,conversationId,body,parentFolderId`
        );
        
        console.log("E-postdata med parentFolderId:", data.value);
        setEmails(data.value);
      } catch (err) {
        setError("Det gick inte att hämta e-postmeddelanden");
        console.error("Error fetching emails:", err);
      } finally {
        setLoading(false);
      }
    };

    if (Object.keys(folderMap).length > 0 && userInfo && startDate && !isRefreshing) {
      fetchEmails();
    }
  }, [callGraphApi, folderMap, userInfo, startDate, isRefreshing]);
  
  // Hantera uppdatering
  const handleRefresh = () => {
    // Använd callGraphApi för alla anrop
    const fetchEmails = async () => {
      if (!startDate) return;

      setLoading(true);
      setError(null);

      try {
        const formattedDate = startDate.toISOString();
        const data = await callGraphApi(
          `/me/messages?$filter=receivedDateTime ge ${formattedDate}&$orderby=receivedDateTime desc&$top=50&$select=id,subject,bodyPreview,receivedDateTime,from,toRecipients,hasAttachments,webLink,importance,conversationId,body,parentFolderId`
        );
        setEmails(data.value);
      } catch (err) {
        setError("Det gick inte att hämta e-postmeddelanden");
        console.error("Error fetching emails:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmails();
  };
  
  return (
    <>
      <EmailDialog
        open={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
        to="optional@default.com"
        subject="Optional default subject"
        body="Optional default body"
      />
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={() => setIsEmailDialogOpen(true)}
        >
          Skicka epost
        </Button>
        <Stack flexDirection={"row"} spacing={1} alignItems={"center"}>
          <DatePicker
            label="Visa från datum"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 200 },
              },
            }}
          />

          <Tooltip title="Uppdatera">
            <IconButton onClick={handleRefresh} disabled={loading || isRefreshing}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {(loading || isRefreshing) ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <EmailMatcher 
          emails={emails} 
          folderMap={folderMap}
          userInfo={userInfo} 
        />
      )}
    </>
  );
};

export default RecentEmails;